'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { popModal } from '@/components/modals'
import { CancelModalBackgroundSimple } from './cancel-modal-background-simple'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

interface CancelConfirmationModalProps {
  subscriptionId: string
  projectId: string
  plan: string
  nextRenewalAt?: number
  isWorkspaceSubscription?: boolean
  onConfirm: (reason?: string) => Promise<void>
}

export function CancelConfirmationModal({
  subscriptionId,
  projectId,
  plan,
  nextRenewalAt,
  isWorkspaceSubscription = false,
  onConfirm,
}: CancelConfirmationModalProps) {
  const t = useTranslations('pricing_v2.cancel_confirmation_modal')
  const [reason, setReason] = useState('')
  const [countdown, setCountdown] = useState(5)
  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // 格式化到期日期
  const formattedEndDate = useMemo(() => {
    if (!nextRenewalAt) return ''
    const date = new Date(nextRenewalAt)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }, [nextRenewalAt])

  // 自动聚焦输入框
  useEffect(() => {
    // 延迟聚焦，确保 modal 动画完成
    const timer = setTimeout(() => {
      textareaRef.current?.focus()
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const trimmedReason = reason.trim()
  const wordCount = trimmedReason.split(/\s+/).filter(Boolean).length
  const reasonIsValid = wordCount >= 10

  const handleConfirmCancel = async () => {
    if (!reasonIsValid) {
      setShowError(true)
      textareaRef.current?.focus()
      return
    }

    try {
      setIsLoading(true)

      // 保存退订理由到数据库
      await fetch('/api/subscription/cancellation-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: subscriptionId,
          project_id: projectId,
          plan: plan.toLowerCase(), // 转换为小写
          reason: trimmedReason,
          canceled_at: new Date().toISOString(),
        }),
      })

      // Send scheduled cancel notification (fire-and-forget)
      fetch('/api/subscription/notify-scheduled-cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId,
          sourceType: isWorkspaceSubscription
            ? 'workspace_subscription'
            : 'project_subscription',
          planName: plan.toLowerCase(),
          reason: trimmedReason,
          ...(isWorkspaceSubscription
            ? { workspaceId: projectId }
            : { projectId }),
        }),
      }).catch(() => {
        // Silently ignore notification errors - don't block cancellation flow
      })

      // 执行退订操作
      await onConfirm(trimmedReason)

      popModal()
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const canCancel = countdown === 0 && !isLoading
  const isSubmitDisabled = !canCancel || !reasonIsValid

  return (
    <DialogContent
      className="w-[95%] max-w-[600px] p-0 rounded-[20px] overflow-hidden !border-0 z-[80] !bg-transparent [&]:shadow-none"
      overlayClassName="z-[79]"
      noCloseButton
      onInteractOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
      style={{ border: 'none', background: 'transparent' }}
    >
      <div className="relative w-full h-full overflow-hidden rounded-[20px]">
        {/* 使用与主页相同的背景图片 */}
        <CancelModalBackgroundSimple />

        {/* 内容区域 */}
        <div className="relative p-8 sm:p-10 flex flex-col gap-6 z-10">
          {/* 关闭按钮已移除 - 用户必须提供原因才能继续 */}

          {/* 标题 */}
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-white leading-[1.6] pr-8">
              {t('title')}
            </DialogTitle>
            <DialogDescription className="text-base text-white/80 leading-[1.6] mt-2">
              {t('description', { date: formattedEndDate })}
            </DialogDescription>
          </DialogHeader>

          {/* 退订理由输入框 */}
          <div>
            <label
              htmlFor="cancel-reason"
              className="block text-sm font-medium text-white mb-2"
            >
              <span>{t('reason_label')}</span>
              <span className="text-red-300 ml-1">*</span>
            </label>
            <Textarea
              ref={textareaRef}
              id="cancel-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                const words = e.target.value
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean).length
                if (showError && words >= 10) {
                  setShowError(false)
                }
              }}
              onBlur={() => {
                const words = reason.trim().split(/\s+/).filter(Boolean).length
                if (words < 10) {
                  setShowError(true)
                }
              }}
              placeholder={t('reason_placeholder')}
              disabled={isLoading}
              required
              maxLength={500}
              className={cn(
                'min-h-[8rem] rounded-lg border bg-gray-800/50 px-4 py-3 text-white placeholder-gray-500 focus-visible:ring-2 focus-visible:border-transparent disabled:opacity-50 resize-none',
                showError && !reasonIsValid
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : 'border-gray-600 focus-visible:ring-blue-500',
              )}
              aria-invalid={showError && !reasonIsValid}
              aria-required={true}
            />
            <div className="flex justify-between text-xs mt-1">
              <span
                className={cn(
                  wordCount < 10 ? 'text-red-400' : 'text-gray-400',
                )}
              >
                {t('word_count', { count: wordCount, min: 10 })}
              </span>
              <span className="text-gray-400">{reason.length}/500</span>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex justify-end items-center pt-2">
            <Button
              type="button"
              onClick={handleConfirmCancel}
              disabled={isSubmitDisabled}
              className="rounded-xl px-8 py-3 bg-[#3A3A3A] text-white font-semibold hover:bg-[#4A4A4A] disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  {t('button_canceling')}
                </>
              ) : countdown > 0 ? (
                t('button_cancel_countdown', { seconds: countdown })
              ) : (
                t('button_cancel')
              )}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}
